import { useRef, useState, useEffect } from "react";
import { itemImage, money } from "@/lib/api";
import { byWearScore } from "@/lib/wear";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Section spotlight - the most-worn pieces of a section as a large-format, scroll-snap strip (native momentum scrolling,
 * trackpad / touch / drag / arrows). Editorial retail feel; the studio renders carry it.
 */
const cpw = (i) => {
  const paid = Number(i.purchase_price ?? i.estimated_retail_price) || 0;
  const w = Number(i.wears_total) || 0;
  return paid > 0 && w > 0 ? paid / w : null;
};

export const Spotlight = ({ items = [], label = "", onSelect, max = 12 }) => {
  const ref = useRef(null);
  const drag = useRef(null);
  const [edges, setEdges] = useState({ start: true, end: false });
  const list = items.slice().sort(byWearScore).slice(0, max);

  const update = () => { const el = ref.current; if (!el) return; setEdges({ start: el.scrollLeft <= 2, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 2 }); };
  useEffect(() => { update(); const el = ref.current; if (!el) return; const ro = new ResizeObserver(update); ro.observe(el); return () => ro.disconnect(); }, [list.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const by = (dir) => { const el = ref.current; if (!el) return; el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.8), behavior: "smooth" }); };
  const onPointerDown = (e) => { if (e.pointerType !== "mouse") return; drag.current = { x: e.clientX, left: ref.current.scrollLeft, moved: 0 }; };
  const onPointerMove = (e) => { const d = drag.current; if (!d) return; const dx = e.clientX - d.x; d.moved = Math.max(d.moved, Math.abs(dx)); ref.current.scrollLeft = d.left - dx; };
  const endDrag = () => { if (drag.current) setTimeout(() => { drag.current = null; }, 0); };

  if (!list.length) return null;
  return (
    <section data-testid="section-spotlight" aria-label={`Most worn in ${label}`}>
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Most worn</p>
          <h2 className="font-display text-[22px] leading-none mt-1">{label}</h2>
        </div>
        <div className="hidden sm:flex items-center gap-1.5">
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-full" onClick={() => by(-1)} disabled={edges.start} aria-label="Scroll back" data-testid="spotlight-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-full" onClick={() => by(1)} disabled={edges.end} aria-label="Scroll forward" data-testid="spotlight-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
      <div
        ref={ref}
        onScroll={update}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        className="mt-4 -mx-1 px-1 flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar cursor-grab active:cursor-grabbing select-none"
        data-testid="spotlight-strip"
      >
        {list.map((it, i) => {
          const c = cpw(it);
          const w60 = Number(it.wears_60d) || 0;
          return (
            <button
              key={it.id}
              type="button"
              onClick={() => { if (!drag.current || drag.current.moved < 6) onSelect?.(it); }}
              className="group snap-start shrink-0 w-[200px] sm:w-[232px] text-left rounded-[var(--radius)] border border-border bg-card overflow-hidden lift focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground"
              data-testid="spotlight-card"
              data-item-id={it.id}
            >
              <div className="relative aspect-[4/5] bg-white">
                <img src={itemImage(it)} alt="" loading="lazy" draggable={false} className="absolute inset-0 h-full w-full object-contain p-4 transition-transform duration-500 ease-out group-hover:scale-[1.04]" />
                <span className="absolute left-3 top-3 font-mono-num text-[10px] tabular-nums text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
                {it.size && <span className="absolute right-3 top-3 rounded-full bg-background/90 backdrop-blur px-2 h-6 inline-flex items-center text-[11px] font-mono-num font-semibold">{it.size}</span>}
              </div>
              <div className="px-4 py-3 border-t border-border">
                <p className="text-sm font-semibold truncate">{it.name || it.subtype || "Piece"}</p>
                <p className="text-xs text-muted-foreground truncate">{[it.brand].filter(Boolean).join(" ")}</p>
                <p className="mt-2 text-[11px] text-muted-foreground font-mono-num tabular-nums">
                  {w60 ? `${w60} wear${w60 === 1 ? "" : "s"} · 60d` : "Not worn yet"}{c != null ? ` · ${money(c)}/wear` : ""}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};
